// hooks/useProductCounter.js
import { useState, useEffect } from 'react';
import { database, ref, set, increment, get } from '../firebase.config';

const PRODUCT_NAMES = [
  "prolab-gb-2",
  "prolab-gb-3", 
  "prolab-gb-4",
  "prolab-gb-custom"
];

export const useProductCounter = () => {
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [years, setYears] = useState([]);

  // Get current year
  const getCurrentYear = () => {
    return new Date().getFullYear().toString();
  };

  // Check if already counted in this session for a specific product
  const isFirstVisit = (productName) => {
    return !sessionStorage.getItem(`productVisitCounted_${productName}`);
  };

  // Increment count for a specific product
  const incrementProductCount = async (productName) => {
    if (!PRODUCT_NAMES.includes(productName)) {
      console.error("Invalid product name");
      return;
    }

    if (!isFirstVisit(productName)) {
      console.log(`Already counted ${productName} in this session`);
      return;
    }

    const year = getCurrentYear();

    try {
      const countRef = ref(database, `productCount/${year}/${productName}`);
      await set(countRef, increment(1));
      sessionStorage.setItem(`productVisitCounted_${productName}`, 'true');
      console.log(`Count incremented for ${productName} ${year}`);
      
      // Update local state
      setTotalCount(prev => prev + 1);
      await extractYears(); // Refresh years list
    } catch (error) {
      console.error("Error incrementing product count:", error);
    }
  };

  // Get all product counts
  const getAllProductCounts = async () => {
    try {
      const snapshot = await get(ref(database, 'productCount'));
      return snapshot.exists() ? snapshot.val() : {};
    } catch (error) {
      console.error("Error getting all product counts:", error);
      return {};
    }
  };

  // Get product counts for a specific year in format [10,20,30,40]
  const getProductCountsForYear = async (year) => {
    try {
      // Initialize array with zeros for all products
      const productCounts = new Array(PRODUCT_NAMES.length).fill(0);
      
      // Get data for the specific year
      const snapshot = await get(ref(database, `productCount/${year}`));
      
      if (snapshot.exists()) {
        const yearData = snapshot.val();
        
        // Fill the array with actual values in order of PRODUCT_NAMES
        PRODUCT_NAMES.forEach((productName, index) => {
          if (yearData[productName]) {
            productCounts[index] = yearData[productName];
          }
        });
      }
      
      return productCounts;
    } catch (error) {
      console.error(`Error getting product counts for ${year}:`, error);
      return new Array(PRODUCT_NAMES.length).fill(0);
    }
  };

  // Extract all available years from the data
  const extractYears = async () => {
    try {
      const allCounts = await getAllProductCounts();
      const yearList = Object.keys(allCounts).sort((a, b) => b - a);
      setYears(yearList);
      return yearList;
    } catch (error) {
      console.error("Error extracting years:", error);
      setYears([]);
      return [];
    }
  };

  // Get total count across all products and years
  const getTotalCount = async () => {
    const allCounts = await getAllProductCounts();
    let total = 0;

    Object.values(allCounts).forEach(yearData => {
      Object.values(yearData).forEach(productCount => {
        total += productCount;
      });
    });

    return total;
  };

  // Fetch and store total count
  const fetchTotalCount = async () => {
    try {
      const total = await getTotalCount();
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching total count:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize - extract years and fetch total count
  useEffect(() => {
    const initialize = async () => {
      await extractYears();
      await fetchTotalCount();
    };
    
    initialize();
  }, []);

  // Helper to increment count based on product slug/case
  const incrementProductCountBySlug = (productSlug) => {
    switch (productSlug) {
      case "prolab-gb-2":
        return incrementProductCount("prolab-gb-2");
      case "prolab-gb-3":
        return incrementProductCount("prolab-gb-3");
      case "prolab-gb-4":
        return incrementProductCount("prolab-gb-4");
      case "prolab-gb-custom":
        return incrementProductCount("prolab-gb-custom");
      default:
        console.error("Unknown product slug:", productSlug);
    }
  };

  return {
    totalCount,
    isLoading,
    years,
    PRODUCT_NAMES,
    incrementProductCount,
    incrementProductCountBySlug, // Use this for your switch case
    getAllProductCounts,
    getProductCountsForYear, // Returns [10,20,30,40] for selected year
    extractYears,
    fetchTotalCount
  };
};